import { filterDOMProps } from '@react-aria/utils';
import { cva, cx } from '@vizit/theme';
import * as React from 'react';
import type { BadgeLevel, BadgeShape } from './types';
import { BadgeSize } from './types';

interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The display level of the badge.
   *
   * @optional
   * @type 'default' | 'information' | 'success' | 'warning' | 'error' | 'dark' | 'slot'
   * @default 'default'
   */
  level?: BadgeLevel;
  /**
   * The shape of the badge.
   *
   * @optional
   * @type 'pill' | 'box'
   * @default 'pill'
   */
  shape?: BadgeShape;
  /**
   * The size of the badge.
   *
   * @optional
   * @type 'xs' | 'sm' | 'md' | 'lg'
   * @default 'md'
   */
  size?: BadgeSize;
  /**
   * Whether the badge has a visible border.
   *
   * @optional
   * @default true
   */
  bordered?: boolean;
  /**
   * Whether the badge has a background fill.
   *
   * @optional
   * @default true
   */
  filled?: boolean;
}

const classes = cva({
  base: 'inline-flex items-center justify-center',
  variants: {
    level: {
      default: 'text-secondary',
      brand: 'text-brand-secondary text-xs',
      information: 'text-orange-700',
      success: 'text-green-primary text-success-primary',
      warning: 'text-starburst-primary text-warning-primary',
      error: 'text-red-primary text-error-primary-hover',
      dark: 'text-primary-on-brand',
      slot: 'text-primary-on-brand',
    },
    shape: {
      pill: 'rounded-full min-w-24',
      box: 'rounded-none',
    },
    size: {
      xs: 'pl-1.5 pr-2 py-0.5 text-xs gap-2',
      sm: 'px-3 py-1 text-xs',
      md: 'px-4 py-1 text-sm',
      lg: 'px-5 py-2 text-sm',
    },
    bordered: {
      true: '',
      false: '',
    },
    filled: {
      true: '',
      false: 'bg-transparent',
    },
  },
  compoundVariants: [
    { level: 'default', bordered: true, class: 'border border-disabled' },
    { level: 'brand', bordered: true, class: 'border border-brand-600' },
    { level: 'information', bordered: true, class: 'border border-orange-200' },
    { level: 'success', bordered: true, class: 'border border-success' },
    { level: 'warning', bordered: true, class: 'border border-warning' },
    { level: 'error', bordered: true, class: 'border border-error' },
    { level: 'dark', bordered: true, class: 'border border-gray-utility-900' },
    { level: 'default', filled: true, class: 'bg-secondary' },
    { level: 'brand', filled: true, class: 'bg-brand-200' },
    { level: 'information', filled: true, class: 'bg-orange-50' },
    { level: 'success', filled: true, class: 'bg-success-solid' },
    { level: 'warning', filled: true, class: 'bg-warning-solid' },
    { level: 'error', filled: true, class: 'bg-error-solid' },
    { level: 'dark', filled: true, class: 'bg-gray-utility-900' },
    { level: 'slot', filled: true, class: 'bg-black/70' },
  ],
  defaultVariants: {
    level: 'default',
    shape: 'pill',
    size: BadgeSize.Md,
    bordered: true,
    filled: true,
  },
});

/**
 * A badge to display a status or label.
 *
 * @param {BadgeProps} - The props to use for the badge.
 * @example
 * <Badge>
 *   <span>Badge</span>
 * </Badge>
 */
const Badge = React.forwardRef<HTMLDivElement, BadgeProps>((props, forwardedRef) => {
  const {
    children,
    className,
    level = 'default',
    shape = 'pill',
    size = BadgeSize.Md,
    bordered = true,
    filled = true,
    style,
    ...other
  } = props;

  const isTextOnly = React.Children.toArray(props.children).every((c) => !React.isValidElement(c));

  return (
    <div
      {...filterDOMProps(other)}
      ref={forwardedRef}
      className={cx(classes({ level, shape, size, bordered, filled }), className)}
      role="presentation"
      style={style}
    >
      {typeof children === 'string' || isTextOnly ? (
        <span className={cx('text-center text-[inherit] fit-content')}>{children}</span>
      ) : (
        children
      )}
    </div>
  );
});

Badge.displayName = 'Badge';

export type { BadgeProps };
export { Badge };
